<template>
  <div class="category-cascader">
    <label v-if="label" :for="id" class="form-label">{{ label }} <span v-if="required" class="text-danger">*</span></label>
    <div class="el-cascader">
      <div class="el-cascader__label" @click="toggleCascader">
        <span v-if="selectedPath.length === 0" class="el-cascader__placeholder">
          {{ placeholder || $t('profile.categories.noParent') }}
        </span>
        <span v-else class="el-cascader__selected">
          {{ selectedPath.map(item => item.name).join(' / ') }}
        </span>
        <button
          v-if="selectedPath.length > 0"
          type="button"
          class="el-cascader__clear-btn"
          :title="$t('common.clear')"
          @click.stop="clearSelection"
        >
          <i class="ti ti-x"></i>
        </button>
        <i class="el-icon-arrow-down"></i>
      </div>
      <div v-if="isCascaderOpen" class="el-cascader__dropdown">
        <div class="el-cascader__search">
          <input
            v-model="searchQuery"
            type="text"
            class="form-control form-control-sm"
            :placeholder="$t('common.search.placeholder')"
          />
        </div>
        <div ref="cascaderPanels" class="el-cascader__panels">
          <div 
            v-for="(level, index) in cascaderLevels" 
            :key="index"
            class="el-cascader__panel"
          >
            <div v-if="levelLoading && levelLoadingIndex === index" class="el-cascader__loading">
              <div class="spinner-border spinner-border-sm text-primary" role="status">
                <span class="visually-hidden">{{ $t('common.loading') }}</span>
              </div>
            </div>
            <ul v-else-if="getFilteredLevel(level).length" class="el-cascader__menu">
              <li 
                v-for="item in getFilteredLevel(level)" 
                :key="item.id"
                class="el-cascader__menu-item"
                :class="{
                  'is-active': isItemSelected(item, index),
                  'is-disabled': isDisabled(item)
                }"
                @click="selectCascaderItem(item, index)"
              >
                <span>{{ item.code }} - {{ item.name }}</span>
                <i 
                  v-if="item.isHaveChild" 
                  class="el-icon-arrow-right ti ti-chevron-right"
                ></i>
              </li>
            </ul>
            <div v-else class="el-cascader__empty">{{ $t('common.noResults') }}</div>
          </div>
        </div>
      </div>
    </div>
    <small v-if="helpText" class="form-text text-muted">
      {{ helpText }}
    </small>
  </div>
</template>

<script>
import { ref, onMounted, watch, nextTick } from 'vue';
import { useI18n } from 'vue-i18n';
import materialCategoryService from '@/services/materialCategoryService';

export default {
  name: 'CategoryCascader',
  props: {
    id: {
      type: String,
      default: () => `category-cascader-${Math.random().toString(36).substr(2, 9)}`
    },
    label: {
      type: String,
      default: ''
    },
    placeholder: {
      type: String,
      default: ''
    },
    helpText: {
      type: String,
      default: ''
    },
    required: {
      type: Boolean,
      default: false
    },
    modelValue: {
      type: String,
      default: ''
    },
    disabledCategoryId: {
      type: String,
      default: ''
    }
  },
  emits: ['update:modelValue', 'category-selected', 'close'],
  setup(props, { emit }) {
    const { t } = useI18n();
    const isCascaderOpen = ref(false);
    const cascaderLevels = ref([[]]);
    const selectedPath = ref([]);
    const levelLoading = ref(false);
    const levelLoadingIndex = ref(0);
    const cascaderPanels = ref(null);
    const searchQuery = ref('');

    // Close cascader when clicking outside
    const closeCascader = () => {
      if (isCascaderOpen.value) {
        isCascaderOpen.value = false;
        searchQuery.value = '';
        emit('close');
      }
    };

    const handleClickOutside = (event) => {
      const cascader = event.target.closest('.category-cascader');
      if (!cascader) {
        closeCascader();
      }
    };

    onMounted(() => {
      document.addEventListener('click', handleClickOutside);
      
      // If we have an initial value, load the category path
      if (props.modelValue) {
        loadCategoryPath(props.modelValue);
      } else {
        // Just load the first level categories
        loadRootCategories();
      }
    });

    // Watch for modelValue changes
    watch(() => props.modelValue, (newValue) => {
      if (newValue && !selectedPath.value.find(cat => cat.id === newValue)) {
        loadCategoryPath(newValue);
      } else if (!newValue) {
        selectedPath.value = [];
      }
    });

    // Methods
    const toggleCascader = () => {
      if (isCascaderOpen.value) {
        closeCascader();
        return;
      }
      isCascaderOpen.value = true;
      searchQuery.value = '';

      // Preserve current selection/path; only load when needed.
      if (!Array.isArray(cascaderLevels.value[0]) || cascaderLevels.value[0].length === 0) {
        loadRootCategories();
      }
      if (props.modelValue && selectedPath.value.length === 0) {
        loadCategoryPath(props.modelValue);
      }
    };

    const loadRootCategories = async () => {
      levelLoading.value = true;
      levelLoadingIndex.value = 0;
      
      try {
        const response = await materialCategoryService.searchCategories({
          page: 0,
          pageSize: 200,
          sorting: 'name asc'
        });
        
        const rootCategories = response.items.map(item => ({
          ...item,
          isExpanded: false,
          children: []
        }));
        
        cascaderLevels.value = [rootCategories];
      } catch (error) {
        console.error('Error loading root categories:', error);
        emit('error', t('profile.categories.loadError'));
      } finally {
        levelLoading.value = false;
      }
    };

    const loadCategoryChildren = async (parent, level) => {
      if (!parent.isHaveChild) return;
      
      levelLoading.value = true;
      levelLoadingIndex.value = level + 1;
      
      // Trim the levels array to current level + 1
      cascaderLevels.value = cascaderLevels.value.slice(0, level + 1);
      // Add a placeholder for the next level
      cascaderLevels.value.push([]);
      
      try {
        const response = await materialCategoryService.searchCategories({
          parentId: parent.id,
          page: 0,
          pageSize: 200,
          sorting: 'name asc'
        });
        let childItems;
        if (Array.isArray(response?.items)) {
          childItems = response.items;
        } else if (Array.isArray(response)) {
          childItems = response;
        } else {
          childItems = [];
        }
        const children = childItems.map(item => ({
          ...item,
          isExpanded: false,
          children: []
        }));
        
        // Update the placeholder with actual children
        if (cascaderLevels.value[level + 1]) {
          cascaderLevels.value[level + 1] = children;
          
          // Scroll the panels to show the new level
          await nextTick();
          if (cascaderPanels.value) {
            cascaderPanels.value.scrollLeft = cascaderPanels.value.scrollWidth;
          }
        } else {
          console.warn(`Cascader level ${level + 1} no longer exists`);
        }
      } catch (error) {
        console.error('Error loading category children:', error);
        emit('error', t('profile.categories.loadChildrenError'));
      } finally {
        levelLoading.value = false;
      }
    };

    const loadCategoryPath = async (categoryId) => {
      if (!categoryId) {
        selectedPath.value = [];
        return;
      }
      
      levelLoading.value = true;
      selectedPath.value = [];
      
      try {
        // First get the target category
        const targetCategory = await materialCategoryService.getCategoryById(categoryId);
        const parentPath = [];
        
        // Add the target to the path
        parentPath.push(targetCategory);
        
        // If there's a parent, build the full path
        let currentParent = targetCategory.parentId;
        while (currentParent) {
          const parent = await materialCategoryService.getCategoryById(currentParent);
          parentPath.unshift(parent); // Add to beginning of array
          currentParent = parent.parentId;
        }
        
        selectedPath.value = parentPath;
        
        // Load the cascader levels based on the path
        await loadRootCategories();
        
        // Sequentially load children for each level in the path
        for (let i = 0; i < parentPath.length - 1; i++) {
          const currentItem = parentPath[i];
          await loadCategoryChildren(currentItem, i);
        }
      } catch (error) {
        console.error('Error loading category path:', error);
        emit('error', t('profile.categories.loadParentPathError'));
      } finally {
        levelLoading.value = false;
      }
    };

    const selectCascaderItem = async (item, level) => {
      // Check if the item is disabled
      if (isDisabled(item)) return;
      
      // Update the selected path
      selectedPath.value = selectedPath.value.slice(0, level);
      selectedPath.value.push(item);
      
      // Emit the selected value
      emit('update:modelValue', item.id);
      emit('category-selected', item);
      
      // If this item has children, load them
      if (item.isHaveChild) {
        await loadCategoryChildren(item, level);
      } else {
        // If it's a leaf node, close the cascader
        closeCascader();
      }
    };

    const isItemSelected = (item, level) => {
      return selectedPath.value[level]?.id === item.id;
    };
    
    const isDisabled = (item) => {
      return props.disabledCategoryId && item.id === props.disabledCategoryId;
    };

    const clearSelection = () => {
      selectedPath.value = [];
      cascaderLevels.value = [[]];
      emit('update:modelValue', '');
      emit('category-selected', null);
      emit('close');
    };

    const getFilteredLevel = (level) => {
      const q = searchQuery.value.trim().toLowerCase();
      if (!q) return level;
      return level.filter((item) =>
        String(item?.name || '').toLowerCase().includes(q) ||
        String(item?.code || '').toLowerCase().includes(q)
      );
    };

    return {
      isCascaderOpen,
      cascaderLevels,
      selectedPath,
      levelLoading,
      levelLoadingIndex,
      cascaderPanels,
      searchQuery,
      toggleCascader,
      selectCascaderItem,
      isItemSelected,
      isDisabled,
      clearSelection,
      getFilteredLevel
    };
  }
};
</script>

<style scoped>
.el-cascader {
  position: relative;
  display: inline-block;
  width: 100%;
}

.el-cascader__label {
  position: relative;
  display: flex;
  align-items: center;
  padding: 0.8rem 2rem 0.8rem 0.75rem;
  font-size: 0.875rem;
  font-weight: 400;
  line-height: 1.5;
  color: #5b6b79;
  background-color: #ffffff;
  background-clip: padding-box;
  border: 1px solid #dbe0e5;
  border-radius: 8px;
  cursor: pointer;
  transition: border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out;
}

.el-cascader__placeholder {
  color: #bec8d0;
}

.el-cascader__selected {
  color: #5b6b79;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.el-icon-arrow-down {
  position: absolute;
  right: 0.75rem;
  top: 50%;
  transform: translateY(-50%);
  transition: transform 0.3s;
}

.el-cascader__clear-btn {
  position: absolute;
  right: 2rem;
  top: 50%;
  transform: translateY(-50%);
  border: 0;
  background: transparent;
  color: #9ca3af;
  padding: 0;
  line-height: 1;
}

.el-cascader__clear-btn:hover {
  color: #6b7280;
}

.el-cascader__dropdown {
  position: absolute;
  z-index: 1000;
  border: 1px solid #e4e7ed;
  border-radius: 4px;
  background-color: #fff;
  box-shadow: 0 2px 12px 0 rgba(0,0,0,.1);
  margin-top: 4px;
  width: max-content;
  max-width: min(90vw, 1400px);
  max-height: 320px;
  display: flex;
  flex-direction: column;
}

.el-cascader__search {
  padding: 0.5rem;
  border-bottom: 1px solid #e4e7ed;
}

.el-cascader__panels {
  display: flex;
  flex-wrap: nowrap;
  max-height: 270px;
  overflow-x: auto;
  overflow-y: auto;
}

.el-cascader__panel {
  width: max-content;
  min-width: 180px;
  max-width: none;
  box-sizing: border-box;
  border-right: 1px solid #e4e7ed;
  max-height: 270px;
  overflow-y: auto;
}

.el-cascader__loading {
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100px;
}

.el-cascader__menu {
  margin: 0;
  padding: 0;
  list-style: none;
}

.el-cascader__menu-item {
  position: relative;
  padding: 0.5rem 1rem;
  cursor: pointer;
  white-space: nowrap;
  color: #606266;
}

.el-cascader__menu-item:hover {
  background-color: #f5f7fa;
}

.el-cascader__menu-item.is-active {
  color: #409eff;
  font-weight: 700;
}

.el-cascader__menu-item.is-disabled {
  color: #c0c4cc;
  cursor: not-allowed;
}

.el-icon-arrow-right {
  position: absolute;
  right: 1rem;
  color: #c0c4cc;
}

.el-cascader__empty {
  padding: 0.75rem;
  color: #9ca3af;
  font-size: 0.875rem;
}
</style> 